import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface CartItem {
  id: string; // varyasyon id'si
  productId: string;
  name: string;
  mlLabel: string;
  price: number;
  quantity: number;
  image?: string | null;
  slug: string;
  kargoUcreti?: number | null;
  campaign?: {
    isActive: boolean;
    type: "TIERED" | "GIFT";
    tiers: Array<{ minQuantity: number; discountPercent: number }>;
    giftNote?: string;
  } | null;
}

interface CartStore {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  removeItem: (variantId: string) => void;
  updateQuantity: (variantId: string, quantity: number) => void;
  clearCart: () => void;
  totalItems: () => number;
  totalPrice: () => number;
  calculateDiscount: (item: CartItem) => number;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      
      calculateDiscount: (item) => {
        const allItems = get().items;
        if (allItems.length === 0) return 0;

        // ─── 1. KADEMELİ İNDİRİM (Kampanya Bazlı) ───
        // Eğer ürüne özel bir kampanya (Örn: 3 al 2 öde) varsa öncelikli uygula
        if (item.campaign && item.campaign.isActive && item.campaign.type === "TIERED" && item.campaign.tiers) {
          const applicableTiers = item.campaign.tiers
            .filter(t => item.quantity >= t.minQuantity)
            .sort((a, b) => b.minQuantity - a.minQuantity);

          if (applicableTiers.length > 0) {
            const bestTier = applicableTiers[0];
            const setCount = Math.floor(item.quantity / bestTier.minQuantity);
            return setCount * (item.price * bestTier.discountPercent / 100);
          }
        }

        // ─── 2. EN UCUZ ÜRÜNE %20 İNDİRİM (Global) ───
        // Sepette birden fazla KALEM ürün varsa, en ucuz olanın BİR tanesine %20 indirim yap.
        if (allItems.length > 1) {
          // Sepetteki tüm ürünleri birim fiyatlarına göre sırala
          const sortedByPrice = [...allItems].sort((a, b) => a.price - b.price);
          const cheapestItem = sortedByPrice[0];

          // Eğer şu an kontrol edilen ürün sepetin en ucuz ürünü ise
          if (item.id === cheapestItem.id) {
            return (item.price * 20) / 100;
          }
        }
        
        return 0;
      },

      addItem: async (newItem) => {
        const currentItems = get().items;
        const existingItem = currentItems.find((item) => item.id === newItem.id);

        if (existingItem) {
          set({
            items: currentItems.map((item) =>
              item.id === newItem.id
                ? { ...item, quantity: item.quantity + newItem.quantity }
                : item
            ),
          });
        } else {
          set({ items: [...currentItems, newItem] });
          // Yeni kalem eklendiğinde sayaç artır (Sadece ilk eklemede)
          try {
            await fetch(`/api/products/${newItem.productId}/stats`, {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ action: "cart" }),
            });
          } catch (e) { console.error("Cart stat error"); }
        }
      },

      removeItem: async (variantId) => {
        const item = get().items.find(i => i.id === variantId);
        set({
          items: get().items.filter((item) => item.id !== variantId),
        });
        
        // Sepetten tamamen çıkarıldığında sayaç azalt
        if (item) {
          try {
            await fetch(`/api/products/${item.productId}/stats`, {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ action: "remove_cart" }),
            });
          } catch (e) { console.error("Remove cart stat error"); }
        }
      },

      updateQuantity: (variantId, quantity) => {
        set({
          items: get().items.map((item) =>
            item.id === variantId ? { ...item, quantity: Math.max(1, quantity) } : item
          ),
        });
      },

      clearCart: () => set({ items: [] }),

      totalItems: () => {
        return get().items.reduce((total, item) => total + item.quantity, 0);
      },

      totalPrice: () => {
        return get().items.reduce((total, item) => {
          const itemTotal = item.price * item.quantity;
          const discount = get().calculateDiscount(item);
          return total + (itemTotal - discount);
        }, 0);
      },
    }),
    {
      name: 'my-parfum-cart',
    }
  )
);
